import React, { useState, useEffect } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';
import languageOptions from '../../config/languageOptions';
import QuestionModal from '../common/QuestionModal';
import Modal from '../common/Modal';
import { cancelSubscription, createSubscription, reactivateSubscription, updateSubscription } from '../../api/userApi';
import PaymentForm from './PaymentForm';
import LoadingOverlay from '../common/LoadingOverlay';
// import './Profile.css';

const formatDate = (d) => {
    const userLocale = navigator.language || "en-US"; // Default to English if locale is not found
    return (new Intl.DateTimeFormat(userLocale, {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(new Date(d)));
  }

export const UpdatingNameGenderDOB = ({userDetails, handleSave, error}) => {
    const { t } = useTranslation();

    const formattedDateOfBirth = userDetails.dateOfBirth
            ? new Date(userDetails.dateOfBirth).toISOString().split('T')[0]
            : '';

    return (
        <form
            className='modal-form'
            onSubmit={(e) => {
            e.preventDefault();
            handleSave({
                name: e.target.name.value,
                dateOfBirth: e.target.dateOfBirth.value,
                gender: e.target.gender.value,
            });
            }}
        >
            <div className='modal-form-input-container'>
            <span>{t('profile.editName')}</span>
            <input type="text" name="name" placeholder={t('profile.editName')} defaultValue={userDetails.name} required />
            </div>
            <div className='modal-form-input-container'>
            <span>{t('profile.dateOfBirth')}</span>
            <input type="date" name="dateOfBirth" placeholder={t('profile.dateOfBirth')} defaultValue={formattedDateOfBirth} />
            </div>
            <div className='modal-form-input-container'>
            <span>{t('profile.gender')}</span>
            <select name="gender" defaultValue={userDetails.gender}>
                <option value="boy">{t('profile.Boy')}</option>
                <option value="girl">{t('profile.Girl')}</option>
                <option value="not communicated">{t('profile.nc')}</option>
            </select>
            </div>
            {error && <p className='form-error'>{error}</p>}
            <button className='big-button action-button' type="submit">
            <i className="fa-solid fa-check"></i>
            {t('profile.save')}
            </button>
        </form>
        );
}

export const UpdatingLanguage = ({userDetails, handleSave, error}) => {
    const { t } = useTranslation();

    return (
        <form
            className='modal-form'
            onSubmit={(e) => {
              e.preventDefault();
              handleSave({
                preferredLanguage: e.target.preferredLanguage.value
              }); }
            }
          >
            <div className='modal-form-input-container'>
              <span>{t('profile.preferredLanguage')}:</span>
              <select
                  name="preferredLanguage"
                  defaultValue={userDetails.preferredLanguage}
              >
                  {languageOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                          {option.label}
                      </option>
                  ))}
              </select>
            </div>
            {error && <p className='form-error'>{error}</p>}
            <button className='big-button action-button' type="submit">
              <i className="fa-solid fa-check"></i>
              {t('profile.save')}
            </button>
          </form>
    )
}

export const UpdatingEmail = ({userDetails, handleSave, error}) => {
    const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
    const [email, setEmail] = useState(userDetails.email);
    const { t } = useTranslation();

    const handleSubmit = () => {
        // get confirmation if updated value = email
        if (email && (email !== userDetails.email || userDetails.changingEmail)) {
            setIsQuestionModalOpen(true);
        }
    }

    return (
        <form
            className='modal-form'
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();}
              }>
            <div className='modal-form-input-container'>
              <span>{t('profile.email')}:</span>
              <input type="email" name="email" placeholder={t('profile.email')} defaultValue={userDetails.email} onChange={(e) => setEmail(e.target.value)} required/>
            </div>
            {error && <p className='form-error'>{error}</p>}
            <button className='big-button action-button' type="submit">
              <i className="fa-solid fa-check"></i>
              {t('profile.save')}
            </button>
            <QuestionModal
                isOpen={isQuestionModalOpen}
                onClose={() => setIsQuestionModalOpen(false)}
                title={t('profile.updatingEmailWarningTitle')}
                message={t('profile.updateingEmailWarningText')}
                onConfirm={() => {setIsQuestionModalOpen(false); handleSave({email: email});}}
                confirmLabel={t('profile.yes')}
                cancelLabel={t('profile.no')}
            ></QuestionModal>
          </form>
    )
}

export const UpdatingPassword = ({handlePasswordSave, error}) => {
    const { t } = useTranslation();

    return (
        <form
        className='modal-form'
        onSubmit={(e) => {
            e.preventDefault();
            handlePasswordSave({
                currentPassword: e.target.currentPassword.value,
                newPassword: e.target.newPassword.value,
                confirmPassword: e.target.confirmPassword.value
            });}
        }>
        <div className='modal-form-input-container'>
            <input type="password" name="currentPassword" placeholder={t('profile.currentPassword')} required/>
        </div>
        <div className='modal-form-input-container'>
            <input type="password" name="newPassword" placeholder={t('profile.newPassword')} required/>
        </div>
        <div>
            <input type="password" name="confirmPassword" placeholder={t('profile.confirmPassword')} required/>
        </div>
        {error && <p className='form-error'>{error}</p>}
        <button className='big-button action-button' type="submit">
            <i className="fa-solid fa-check"></i>
            {t('profile.save')}
        </button>
        </form>
    );
}

// 🔹 Custom styling for PaymentElement
const appearance = {
  theme: "night", // Options: 'stripe', 'flat', 'night', 'none'
  variables: {
      colorPrimary: "#3DDC97",
      colorText: "#F4F4F4",
      fontFamily: "'Varela Round', sans-serif",
      borderRadius: "1.8em",
  },
  rules: {
      ".Input": {
          color: "#ffffff",
          fontSize: "1rem",
      },
      ".Label": {
          color: "#B0B3C7",
      },
  },
};

const plans = [
  { id: "free", name: "subscription.FreePlanName", price: "subscription.freePlanPrice", description: "subscription.freePlanDescription", isPaid: false },
  { id: "basic", name: "subscription.BasicPlanName", price: "subscription.basicPlanPrice", description: "subscription.basicPlanDescription", isPaid: true },
  { id: "premium", name: "subscription.PremiumPlanName", price: "subscription.premiumPlanPrice", description: "subscription.premiumPlanDescription", isPaid: true }
];

export const UpdatingSubscriptionLevel = ({userDetails, stripePromise, handleClose}) => {
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [showPayment, setShowPayment] = useState(false);
    const [loading, setLoading] = useState(false);
    const [cancellingPlan, setCancellingPlan] = useState(false);
    const [clientSecret, setClientSecret] = useState("");
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isChangingSubscriptionModal, setIsChangingSubscriptionModal] = useState(false);
    const [isCancellingSubscriptionModal, setIsCancellingSubscriptionModal] = useState(false);
    const [isMessageModal, setIsMessageModal] = useState(false);
    const [messageModal, setMessageModal] = useState('');
    const { t } = useTranslation();
    const [updatingMode, setUpdatingMode] = useState("sameSubscription"); // "newSubscription", "cancelSubscription", "updateSubscription"

    useEffect(() => {
        if (userDetails && userDetails.subscriptionLevel) {
            setSelectedPlan(plans.find((plan) => plan.id === userDetails.subscriptionLevel));
            if (userDetails.subscriptionEndDate) {
                setCancellingPlan(true);
            }
        }
    }, [userDetails]);

    const handleSelectPlan = async (plan) => {
        if (plan.id !== selectedPlan?.id) {
            setShowPayment(false);
            setCancellingPlan(false);
        }
        setSelectedPlan(plan);

        if (userDetails.subscriptionLevel === 'free') {
            if (plan.isPaid) {
                setMessage(t('subscription.newSubscription'));
                setUpdatingMode("newSubscription");
            } else {
                setMessage(t('subscription.sameSubscription'));
                setUpdatingMode("sameSubscription");
            }
        } else {
            if (plan.id === userDetails.subscriptionLevel) {
                if (userDetails.subscriptionEndDate) {
                    setMessage(null);
                    setCancellingPlan(true);
                } else {
                    setMessage(t('subscription.sameSubscription'));
                }
                setUpdatingMode("sameSubscription");
            } else if (plan.id === 'free') {
                if (userDetails.subscriptionEndDate) {
                    setMessage(null);
                    setCancellingPlan(true);
                    setUpdatingMode("sameSubscription");
                } else {
                    setMessage(t('subscription.cancelSubscription'));
                    setUpdatingMode("cancelSubscription");
                }
            } else {
                setMessage(t('subscription.updateSubscription'));
                setUpdatingMode("updateSubscription");
            }
        }
    };

    const handleOK = async () => {
        switch (updatingMode) {
            case "sameSubscription": 
                return handleClose();
            case "cancelSubscription":
                setIsCancellingSubscriptionModal(true);
                break;
            case "newSubscription":
                return await handleNewSubscription();
            case "updateSubscription":
                setIsChangingSubscriptionModal(true);
                break;
            default:
                break;
        }
    }

    const handleCancelSubscription = async () => {
        setError(null);
        setIsCancellingSubscriptionModal(false);
        try {
            setLoading(true);
            const cancelResult = await cancelSubscription();
            setLoading(false);
            setMessageModal(t('subscription.cancellingSubscriptionSuccessful', {subscriptionEndDate: formatDate(cancelResult.subscriptionEndDate)}));
            setIsMessageModal(true);
        } catch (error) {
            setLoading(false);
            console.error('Error cancelling subscription', error);
            setError(t('subscription.errorCancellingSubscription'));
        }
    }

    const handleReactivateSubscription = async () => {
        setError(null);
        try {
            setLoading(true);
            await reactivateSubscription();
            setLoading(false);
            setMessageModal(t('subscription.reactivatingSubscriptionSuccessful'));
            setIsMessageModal(true);
        } catch (error) {
            console.error('Error reactivating subscription', error);
            setLoading(false);
            setError(t('subscription.errorReactivatingSubscription'));
        }
    }

    const handleNewSubscription = async () => {
        setError(null);
        try {
            setLoading(true);
            // 🔹 Get the payment intent client secret
            const subscription = await createSubscription({ userId: userDetails._id, subscriptionLevel: selectedPlan.id });
            const { clientSecret } = subscription;
            setClientSecret(clientSecret);
            setShowPayment(true);
        } catch (error) {
            console.error('Error creating subscription', error);
            setError(t('subscription.errorCreatingSubscription'));
        } finally {
            setLoading(false);
        }
    }

    const handleUpdateSubscription = async () => {
        setError(null);
        setIsChangingSubscriptionModal(false);
        try {
            setLoading(true);
            const updateResult = await updateSubscription( {subscriptionLevel: selectedPlan.id} );
            setLoading(false);
            setMessageModal(t('subscription.updatingSubscriptionSuccessful', {subscriptionLevel: updateResult.subscriptionLevel}));
            setIsMessageModal(true);
        } catch (error) {
            setLoading(false);
            console.error('Error updating subscription', error);
            setError(t('subscription.errorUpdatingSubscription'));
            setSelectedPlan(plans.find((plan) => plan.id === userDetails.subscriptionLevel));
        }
    }

    const closePaymentForm = (reload = false) => {
        setShowPayment(false);
        handleClose(reload);
    }

    return (
        <>
        <label>{t('profile.subscriptionLevel')} <strong>{t(`subscription.${userDetails.subscriptionLevel}`)}</strong></label>
        <div className="plans-container">
          {plans.map(plan => (
            <div key={plan.id} className={`plan-card ${selectedPlan?.id === plan.id ? "selected" : ""}`}>
                <h3>{t(plan.name)}</h3>
                <p>{t(plan.description)}</p>
                <strong>{t(plan.price)}</strong>
                <button onClick={() => handleSelectPlan(plan)} disabled={selectedPlan?.id === plan.id}>
                    {selectedPlan?.id === plan.id ? t('subscription.selected') : t('subscription.selectPlan')}
                </button>
            </div>
            ))}
        </div>
        {message && <p className="message">{message}</p>}
        {cancellingPlan && userDetails.subscriptionLevel !== 'free' && userDetails.subscriptionEndDate && (new Date(userDetails.subscriptionEndDate) > new Date()) && (
            <>
              <p className='user-details-subscription-end-date'>{t('subscription.subscriptionEndDateInUpdating', {subscriptionLevel: userDetails.subscriptionLevel, subscriptionEndDate: formatDate(userDetails.subscriptionEndDate)})}</p>
              <button className='link-button' onClick={handleReactivateSubscription}>{t('subscription.reactivateSubscription')}</button>
            </>
        )}
        {loading && <LoadingOverlay />
}
        {!showPayment && (
            <div className='modal-buttons'>
                <button className='action-btn' onClick={handleOK}>
                    <i className="fa-solid fa-check"></i>
                    {t('profile.ok')}
                </button>
            </div>
        )}
        {showPayment && clientSecret && (
          <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
            <PaymentForm onClose={closePaymentForm} />
          </Elements>
        )}
        {error && <p className="form-error">{error}</p>}
        <QuestionModal
            isOpen={isChangingSubscriptionModal}
            onClose={() => setIsChangingSubscriptionModal(false)}
            title={t('subscription.updatingSubscriptionWarningTitle')}
            message={t('subscription.updatingSubscriptionWarningText', {subscriptionLevel: selectedPlan?.id})}
            onConfirm={handleUpdateSubscription}
            confirmLabel={t('profile.yes')}
            cancelLabel={t('profile.no')}
        ></QuestionModal>
        <QuestionModal
            isOpen={isCancellingSubscriptionModal}
            onClose={() => setIsCancellingSubscriptionModal(false)}
            title={t('subscription.cancellingSubscriptionWarningTitle')}
            message={t('subscription.cancellingSubscriptionWarningText')}
            onConfirm={handleCancelSubscription}
            confirmLabel={t('profile.yes')}
            cancelLabel={t('profile.no')}
        ></QuestionModal>

        <Modal isOpen={isMessageModal} onClose={() => {setIsMessageModal(false); handleClose(true);}}>
            <p className='modal-message'>{messageModal}</p>
            <button className='action-btn' onClick={() => {setIsMessageModal(false); handleClose(true);}}>
                <i className="fa-solid fa-check"></i>
                {t('profile.ok')}
            </button>
        </Modal>

        </>
    );
}